import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { fetchStandaloneBlock } from '../../../../utils/services/complexDocumentsAPI';
import {
  detectAndSplit,
  getHybridBatchList,
  getDocument,
  previewPdf,
  updateHybridBatch,
  submitBatch,
} from '../../../../utils/services/hybridMailAPI';
import { base64ToPdfBlob } from '../../../../utils/helper';
import { updateEnvelopeAddress } from '../../../../utils/services/hybridMailAPI';
import { measurementUnitConversion } from '../../../../utils/javascript';
import { fetchOrganizationInserts, uploadInsert } from '../../../../utils/services/insertsApi';
import { FontsContext } from '../../../../context/fontsContext';
import { INSERTS_TAB } from '../../../ComplexDocumentDesign/ComplexPdfInserts/translationValues';
import { HYBRID_MAIL } from './TranslationValue';

const HybridMailContext = createContext();

export const PDF_SCALE = 1.5;
export const scaleValue = val => val * PDF_SCALE;

export const ENVELOPE_TYPES = {
  DL: {
    x: scaleValue(120.94488188976379),
    y: scaleValue(211.65354330708664),
    width: scaleValue(359.05511811023626),
    height: scaleValue(143.62204724409452),
  },
  C5: {
    x: scaleValue(83.1496062992126),
    y: scaleValue(226.7716535433071),
    width: scaleValue(359.05511811023626),
    height: scaleValue(143.62204724409452),
  },
  C4: {
    x: scaleValue(98.26771653543308),
    y: scaleValue(253.22834645669295),
    width: scaleValue(359.05511811023626),
    height: scaleValue(143.62204724409452),
  },
};

const acceptedExtension = ['pdf'];

export const HybridMailProvider = ({
  children,
  hybridInfo,
  setHybridInfo,
  onRefresh,
  handleCurrentPage,
  t,
}) => {
  const { batchName, splitOnCriteria, pageCount, uploadedFile } = hybridInfo;
  const { fontList } = useContext(FontsContext);

  const [selectedFile, setSelectedFile] = useState(null);
  const [envelopeType, setEnvelopeType] = useState('C5');
  const [shiftSize, setShiftSize] = useState('');
  const [shiftSizePx, setShiftSizePx] = useState(0);
  const [shiftAllVal, setShiftAllVal] = useState(0);
  const [shiftAll, setShiftAll] = useState(false);
  const [shiftUnit, setShiftUnit] = useState('px');
  const [deliveryClass, setDeliveryClass] = useState('First Class');
  const [selectedStationery, setSelectedStationery] = useState(null);
  const [selectedInsert, setSelectedInsert] = useState(null);
  const [stationeryList, setStationeryList] = useState([]);
  const [insertList, setInsertList] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [addressText, setAddressText] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [dragSize, setDragSize] = useState({ width: 200, height: 80 });
  const [isValidPos, setIsValidPos] = useState(true);
  const [isPreview, setIsPreview] = useState(false);
  const [addressBlocks, setAddressBlocks] = useState([]);
  const [selectedBatchDetailId, setSelectedBatchDetailId] = useState(0);
  const [fontStyle, setFontStyle] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [isSchedulingModalVisible, setIsSchedulingModalVisible] = useState(false);
  const [position, setPosition] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [listLimit, setListLimit] = useState(10);
  const [showRefresh, setShowRefresh] = useState(false);

  const fileInputRef = useRef(null);
  const documentListRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const itemRefs = useRef([]);
  const debounceTimeoutRef = useRef(null);

  const envelopeTypeChange = useCallback(
    async value => {
      setEnvelopeType(value);
      setAddressBlocks(prev =>
        prev.map(item => ({
          ...item,
          addressBlock: {
            ...item.addressBlock,
            x: null,
            y: null,
          },
        })),
      );
      const env = ENVELOPE_TYPES[value];
      setPosition({ x: env.x + 5, y: env.y + 5 });
      await updateHybridBatch({ batchId: batchId, envelopeType: value });
    },
    [batchId],
  );

  const deliveryClassChange = useCallback(
    async value => {
      setDeliveryClass(value);
      await updateHybridBatch({ batchId: batchId, deliveryClass: value });
    },
    [batchId],
  );

  const getAddressById = useCallback(
    (id, direction = 0) => {
      const currentIndex = addressBlocks.findIndex(item => item.batchDetailId === id);

      if (currentIndex === -1) {
        return null; // id not found
      }

      // If no direction provided → return exact match
      if (!direction) {
        return addressBlocks[currentIndex];
      }

      const newIndex = currentIndex + direction;

      // Boundary check
      if (newIndex < 0 || newIndex >= addressBlocks.length) {
        return null;
      }

      return addressBlocks[newIndex];
    },
    [addressBlocks],
  );

  const handleStationaryOrPreview = useCallback(
    async (
      isPreview = true,
      isStationery = true,
      isNavigation = false,
      fileName = undefined,
      stationeryValue = selectedStationery,
      insertValue = selectedInsert,
      shiftValue = undefined,
      batchDetailId = selectedBatchDetailId,
    ) => {
      if (!addressBlocks.length > 0) return;
      if (isPreview) {
        setIsLoading(true);
        setIsPreview(true);
      }
      const env = ENVELOPE_TYPES[envelopeType];
      const addressBlock = getAddressById(batchDetailId);
      const fileInfo = addressBlock.fileName;
      const addressInfo = addressBlock.addressBlock;
      delete addressInfo.lineSpacing;
      let insertObj;
      if (insertValue) {
        insertObj = insertList.find(insert => insert.id === insertValue);
      }
      // if (!shiftValue) {
      //   shiftValue = shiftAll ? shiftAllVal : shiftSize.get(blockIndex) ?? 0;
      // }
      const json = {
        batchId: Number(batchId),
        fileName: fileName ?? fileInfo,
        skipAddress: !isPreview,
        shiftBelowY: shiftValue,
        stationaryId: isStationery ? stationeryValue : null,
        letter:
          isPreview || shiftValue
            ? Object.assign({}, addressInfo, {
                x: (addressInfo?.x ?? env.x + 5) / PDF_SCALE,
                y: (addressInfo?.y ?? env.y + 5) / PDF_SCALE + 8,
              })
            : undefined,
        ...(insertValue && insertObj && { insert: insertObj }),
      };

      const res = await previewPdf(json);
      if (isNavigation) {
        return res;
      } else {
        setSelectedFile(res.url);
        const addressXY = {
          x: addressBlock?.addressBlock?.x ?? env.x + 5,
          y: addressBlock?.addressBlock?.y ?? env.y + 5,
        };
        setPosition(addressXY);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    },
    [
      batchId,
      addressBlocks,
      selectedBatchDetailId,
      selectedStationery,
      selectedInsert,
      shiftSize,
      shiftUnit,
      shiftAll,
      shiftAllVal,
      envelopeType,
    ],
  );

  const handleStationery = async value => {
    setSelectedStationery(value);
    setIsLoading(true);
    await updateHybridBatch({ batchId: batchId, stationaryId: value });
    await handleStationaryOrPreview(false, true, false, undefined, value);
  };

  const handleInsert = async value => {
    setSelectedInsert(value);
    setIsLoading(true);
    await updateHybridBatch({ batchId: batchId, insertsId: value });
    await handleStationaryOrPreview(false, true, false, undefined, selectedStationery, value);
  };

  const handleApplyAllShift = e => {
    const checked = e.target.checked;
    setShiftAll(checked);
    const currentValue = shiftSize ?? 0;
    const finalValue = checked ? currentValue : shiftAllVal;
    const pxVal = measurementUnitConversion(value, shiftUnit, 'px');
    if (checked) {
      setShiftAllVal(currentValue);
    }
    setShiftSize(finalValue);
    setShiftSizePx(pxVal);
  };

  const handleShitContent = async val => {
    const value = val === '' ? 0 : parseFloat(val);
    if (Number.isNaN(value)) return;
    const pxVal = measurementUnitConversion(value, shiftUnit, 'px');
    setShiftSize(val);
    setShiftSizePx(pxVal);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      setIsLoading(true);
      await updateHybridBatch({ batchDetailId: selectedBatchDetailId, shiftContent: pxVal });
      handleStationaryOrPreview(
        false,
        true,
        false,
        undefined,
        selectedStationery,
        selectedInsert,
        pxVal,
      );
    }, 1000);
  };

  const handleShiftUnit = async val => {
    const previousUnit = shiftUnit;
    setShiftUnit(val);
    const value = shiftAll ? shiftAllVal : shiftSize ?? 0;
    const updatedValue = measurementUnitConversion(value, previousUnit, val);
    const pxVal = measurementUnitConversion(value, val, 'px');
    if (shiftAll) {
      setShiftAllVal(updatedValue);
    }
    setShiftSize(prev => (prev === '' ? prev : updatedValue));
    setShiftSizePx(pxVal);
    await updateHybridBatch({ batchDetailId: selectedBatchDetailId, unit: val });
  };

  const handleExitPreview = async () => {
    setIsLoading(true);
    await handleStationaryOrPreview(false, true, false);
    setTimeout(() => {
      setIsPreview(false);
    }, 200);
  };

  const setPdfFile = useCallback(
    async (params, batchDetailId) => {
      const shiftSizeVal = shiftAll ? shiftAllVal : shiftSize ?? '0';
      if (selectedStationery || isPreview || selectedInsert || shiftSizeVal) {
        const res = await handleStationaryOrPreview(
          isPreview,
          true,
          true,
          params.fileName,
          selectedStationery,
          selectedInsert,
          shiftSizeVal,
          batchDetailId,
        );
        return res.url;
      } else {
        const { base64Content, addressX, addressY } = await getDocument(params);
        const env = ENVELOPE_TYPES[envelopeType];
        const addressXY = { x: addressX ?? env.x + 5, y: addressY ?? env.y + 5 };
        setPosition(addressXY);
        checkValidPosition(addressXY.x, addressXY.y);
        const blob = base64ToPdfBlob(base64Content);
        const url = URL.createObjectURL(blob);
        return url;
      }
    },
    [
      shiftAll,
      shiftAllVal,
      shiftSize,
      selectedBatchDetailId,
      selectedStationery,
      isPreview,
      selectedInsert,
      envelopeType,
    ],
  );

  const detectAddresses = useCallback(
    async file => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('batchName', batchName);
      formData.append('splitType', splitOnCriteria);
      formData.append('envelopeType', 'C5');
      formData.append('deliveryClass', 'First Class');
      if (splitOnCriteria === 'pageCount') {
        formData.append('pageCount', pageCount);
      }

      try {
        const {
          data: { batchId, files, splittingCompleted },
        } = await detectAndSplit(formData);
        setBatchId(batchId);
        setShowRefresh(splittingCompleted);
        if (batchId && files.length) {
          const pdfUrl = await setPdfFile({
            batchDetailId: files[0].batchDetailId,
            fileName: files[0].fileName,
          });
          setSelectedFile(pdfUrl);
        }
        const blocks = files;
        setAddressBlocks(blocks || []);
        if (blocks?.length) {
          const addressBlock = blocks[0].addressBlock;
          setSelectedBatchDetailId(blocks[0].batchDetailId);
          setAddressText(addressBlock.text);
          const env = ENVELOPE_TYPES[envelopeType];
          const addressXY = { x: addressBlock?.x ?? env.x + 5, y: addressBlock?.y ?? env.y + 5 };
          setPosition(addressXY);
          setFontStyle(blocks[0].addressBlock);
        }
      } catch (error) {
        message.error('Address detection failed');
        console.error('Address detection error:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [batchName, envelopeType, splitOnCriteria, pageCount],
  );

  const submitHybridBatch = async () => {
    try {
      const payload = {}
      const res = await submitBatch(payload, batchId);      
    } catch (error) {
      console.error('Error processing text:', error);
    }
  };

  

  const handleSendLetter = async () => {
    // if (!selectedFile || !isValidPos) return;
    submitHybridBatch()
    setIsSchedulingModalVisible(true);
  };

  const handleProcessSelectedText = async () => {
    if (!selectedText) return;
    setIsLoading(true);
    try {
      const selectedPdf = addressBlocks[selectedBatchDetailId];
      const payload = {
        batchId: batchId,
        fileName: selectedPdf?.fileName,
        startPage: selectedPdf.startPage,
        endPage: selectedPdf.endPage,
        correctAddressText: selectedText,
        previousAddressBlock: selectedPdf?.addressBlock,
      };
      const res = await updateEnvelopeAddress(payload);
      if (res.status === 'SUCCESS') {
        setAddressBlocks(prev =>
          prev.map((item, i) =>
            i === selectedBatchDetailId
              ? {
                  ...item,
                  addressBlock: {
                    ...item.addressBlock,
                    ...res.newAddressBlock,
                    x: item.addressBlock.x,
                    y: item.addressBlock.y,
                  },
                }
              : item,
          ),
        );
        setAddressText(res.newAddressBlock.text);
        const pdfUrl = await setPdfFile({
          batchDetailId: res.batchDetailId,
          fileName: res.fileName,
        });
        setSelectedFile(pdfUrl);
      }
    } catch (error) {
      console.error('Error processing text:', error);
    } finally {
      setIsLoading(false);
      setSelectedText('');
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
    }
  };

  const handleNavigation = async (direction, batchDetailId = null) => {
    let currentAddress;
    if (direction === 'click' && batchDetailId) {
      if (selectedBatchDetailId === batchDetailId) return;
      currentAddress = getAddressById(batchDetailId);
    } else {
      // newIndex = selectedBatchDetailId + direction;
    }

    setIsLoading(true);
    const pdfUrl = await setPdfFile(
      { batchDetailId, fileName: currentAddress.fileName },
      currentAddress.batchDetailId,
    );
    setTimeout(() => {
      setSelectedBatchDetailId(currentAddress.batchDetailId);
      setSelectedFile(pdfUrl);
      setAddressText(currentAddress.addressBlock.text);
      setFontStyle(currentAddress.addressBlock);
      const env = ENVELOPE_TYPES[envelopeType];
      const addressXY = {
        x: currentAddress.addressBlock?.x ?? env.x + 5,
        y: currentAddress.addressBlock?.y ?? env.y + 5,
      };
      setPosition(addressXY);
      setIsLoading(false);
    }, 500);
  };

  const handleRefreshList = useCallback(
    async (pageNumber = undefined, searchValue = undefined) => {
      try {
        setIsLoading(true);
        const {
          data: { files, splittingCompleted },
          dataCount,
        } = await getHybridBatchList({ hybridBatchId: batchId, page: pageNumber ?? currentPage, text: searchValue });
        const totalData = dataCount;
        const totalPages = Math.ceil(totalData / listLimit);
        if (files.length && totalData && !isNaN(totalPages)) {
          const pdfUrl = await setPdfFile({
            batchDetailId: files[0].batchDetailId,
            fileName: files[0].fileName,
          });
          setSelectedFile(pdfUrl);
          const addressBlock = files[0].addressBlock;
          setSelectedBatchDetailId(files[0].batchDetailId);
          setAddressText(addressBlock.text);
          const env = ENVELOPE_TYPES[envelopeType];
          const addressXY = { x: addressBlock?.x ?? env.x + 5, y: addressBlock?.y ?? env.y + 5 };
          setPosition(addressXY);
          setFontStyle(files[0].addressBlock);
          setAddressBlocks(files);
          setTotalRecords(totalData);
          setTotalPages(totalPages);
          setShowRefresh(splittingCompleted);
        }
      } catch (error) {
        console.error('Error refreshing list:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [batchId],
  );

  // const getBatchDetails = useCallback(async batchId => {
  //   try {
  //     setIsLoading(true);
  //     const {
  //       data: { files, splittingCompleted },
  //       dataCount,
  //     } = await getHybridBatchList({ batchId, page: 1 });
  //     const totalData = dataCount;
  //     const totalPages = Math.ceil(totalData / listLimit);
  //     if (files.length && totalData && !isNaN(totalPages)) {
  //       const pdfUrl = await setPdfFile({
  //         batchDetailId: files[0].batchDetailId,
  //         fileName: files[0].fileName,
  //       });
  //       setSelectedFile(pdfUrl);
  //     }
  //     const blocks = files;
  //     setAddressBlocks(blocks || []);
  //     if (blocks?.length) {
  //       const addressBlock = blocks[0].addressBlock;
  //       setSelectedBatchDetailId(blocks[0].batchDetailId);
  //       setAddressText(addressBlock.text);
  //       const env = ENVELOPE_TYPES[envelopeType];
  //       const addressXY = { x: addressBlock?.x ?? env.x + 5, y: addressBlock?.y ?? env.y + 5 };
  //       setPosition(addressXY);
  //       setFontStyle(blocks[0].addressBlock);
  //       setAddressBlocks(files);
  //       setTotalRecords(totalData);
  //       setTotalPages(totalPages);
  //       setShowRefresh(splittingCompleted);
  //     }
  //   } catch (error) {
  //     console.error('Error refreshing list:', error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, []);

  const handleOnChangePage = (type, value) => {
    if (type === 'input') {
      const pageNumber = Number(value);
      if (!isNaN(pageNumber) && pageNumber > 0) {
        setCurrentPage(pageNumber);
        handleRefreshList(pageNumber);
      } else {
        setCurrentPage(value);
        return;
      }
    }

    if (type === 'prev') {
      const updatedPage = currentPage > 1 ? currentPage - 1 : 1;
      setCurrentPage(updatedPage);
      handleRefreshList(updatedPage);
    }

    if (type === 'next') {
      const updatedPage = currentPage + 1;
      setCurrentPage(updatedPage);
      handleRefreshList(updatedPage);
    }
  };

  const handleInputSearch = useCallback((value) => {
    if (!batchId) return;
    console.log(value);
    handleRefreshList(undefined, value);
  }, [batchId, handleRefreshList]);

  const updateXYByIndex = async (newX, newY) => {
    setPosition({ x: newX, y: newY });
    setAddressBlocks(prev =>
      prev.map(item =>
        item.batchDetailId === selectedBatchDetailId
          ? {
              ...item,
              addressBlock: {
                ...item.addressBlock,
                x: newX,
                y: newY,
              },
            }
          : item,
      ),
    );
    checkValidPosition(newX, newY);
    await updateHybridBatch({
      batchDetailId: selectedBatchDetailId,
      addressX: newX,
      addressY: newY,
    });
  };

  const checkValidPosition = useCallback(
    (x, y) => {
      const env = ENVELOPE_TYPES[envelopeType];
      const { width, height } = dragSize;
      const inside =
        x >= env.x &&
        y >= env.y &&
        x + width <= env.x + env.width &&
        y + height <= env.y + env.height;

      setIsValidPos(inside);
    },
    [envelopeType, dragSize],
  );

  const updateAddressText = async val => {
    setAddressText(val);
    setAddressBlocks(prev =>
      prev.map(item =>
        item.batchDetailId === selectedBatchDetailId
          ? {
              ...item,
              addressBlock: {
                ...item.addressBlock,
                text: val,
              },
            }
          : item,
      ),
    );

    await updateHybridBatch({ batchDetailId: selectedBatchDetailId, address: val });
  };

  const handleInsertClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      message.error(
        t(
          INSERTS_TAB.UPLOADED_FILE_EXTENSION_ERROR_MESSAGE.key,
          INSERTS_TAB.UPLOADED_FILE_EXTENSION_ERROR_MESSAGE.value,
          { value: acceptedExtension.join(', ') },
        ),
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setUploadLoading(true);
    try {
      const response = await uploadInsert(file);
      if (response.statusCode === 125) {
        const insertResponse = await fetchOrganizationInserts();
        if (insertResponse?.statusCode === 129 && insertResponse?.data) {
          const listOfInserts = [{ id: null, name: t(HYBRID_MAIL.NONE_OPTION.key, HYBRID_MAIL.NONE_OPTION.value) }, ...insertResponse.data];
          setInsertList(listOfInserts);
        }
        message.success(
          t(INSERTS_TAB.INSERT_UPLOAD_SUCCESS.key, INSERTS_TAB.INSERT_UPLOAD_SUCCESS.value),
        );
      }
    } catch (error) {
      console.error('Upload failed:', error);
      message.error(
        t(INSERTS_TAB.INSERT_UPLOAD_FAILURE.key, INSERTS_TAB.INSERT_UPLOAD_FAILURE.value),
      );
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (uploadedFile) {
      detectAddresses(uploadedFile);
    }
  }, [uploadedFile]);

  // useEffect(() => {
  //   if(hybridBatchId && !uploadedFile){
  //     setBatchId(hybridBatchId);
  //     getBatchDetails(hybridBatchId);
  //   }
  // }, [hybridBatchId])

  useEffect(() => {
    const pos = getAddressById(selectedBatchDetailId)?.addressBlock;
    const env = ENVELOPE_TYPES[envelopeType];
    checkValidPosition(pos?.x ?? env.x + 5, pos?.y ?? env.y + 5);
  }, [dragSize, envelopeType, selectedBatchDetailId]);

  useEffect(() => {
    if (!addressText) return;

    const lines = addressText.split('\n');
    setDragSize({
      width: Math.max(180, Math.max(...lines.map(l => l.length)) * 8 + 20),
      height: Math.max(60, lines.length * 20 + 20),
    });
  }, [addressText]);

  // useEffect(() => {
  //   if (selectedBatchDetailId >= 0 && itemRefs.current[selectedBatchDetailId]) {
  //     itemRefs.current[selectedBatchDetailId].scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'nearest',
  //     });
  //   }
  // }, [selectedBatchDetailId]);

  useEffect(() => {
    (async () => {
      const response = await fetchStandaloneBlock({
        tags: 'stationery',
      });
      if (response?.statusCode === 2505 && response.data) {
        const listOfStationeries = [
          { parentBlockName: t(HYBRID_MAIL.NONE_OPTION.key, HYBRID_MAIL.NONE_OPTION.value), parentBlockId: null },
          ...response.data,
        ];
        setStationeryList(listOfStationeries);
      }

      const insertResponse = await fetchOrganizationInserts();
      if (insertResponse?.statusCode === 129 && insertResponse?.data) {
        const listOfInserts = [{ id: null, name: t(HYBRID_MAIL.NONE_OPTION.key, HYBRID_MAIL.NONE_OPTION.value) }, ...insertResponse.data];
        setInsertList(listOfInserts);
      }
    })();
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setSelectedText('');
        return;
      }

      const range = selection.getRangeAt(0);
      const container = pdfContainerRef.current;

      if (container && container.contains(range.commonAncestorContainer)) {
        const text = selection.toString().trim();
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, []);

  useEffect(() => {
    if (fontList.qlFonts && fontStyle?.fontName) {
      const isFontPresent = fontList.qlFonts.includes(fontStyle.fontName);
      if (!isFontPresent) {
        message.open({
          type: 'warning',
          content: t(HYBRID_MAIL.FONT_NOT_AVAILABLE.key, HYBRID_MAIL.FONT_NOT_AVAILABLE.value, { fontName: fontStyle.fontName }),
          duration: 4,
        });
      }
    }
  }, [fontStyle, fontStyle.fontName, fontList.qlFonts]);

  const value = {
    selectedFile,
    envelopeType,
    shiftSize,
    shiftAllVal,
    shiftAll,
    shiftUnit,
    deliveryClass,
    selectedStationery,
    selectedInsert,
    stationeryList,
    insertList,
    numPages,
    batchId,
    addressText,
    uploadLoading,
    dragSize,
    isValidPos,
    isPreview,
    addressBlocks,
    selectedBatchDetailId,
    fontStyle,
    isLoading,
    selectedText,
    isSchedulingModalVisible,
    fileInputRef,
    documentListRef,
    itemRefs,
    position,
    showRefresh,
    setPosition,
    envelopeTypeChange,
    deliveryClassChange,
    handleStationery,
    handleInsert,
    handleApplyAllShift,
    handleShitContent,
    handleShiftUnit,
    handleStationaryOrPreview,
    handleExitPreview,
    detectAddresses,
    handleSendLetter,
    handleProcessSelectedText,
    handleNavigation,
    updateXYByIndex,
    updateAddressText,
    handleInsertClick,
    handleFileChange,
    setIsSchedulingModalVisible,
    onRefresh,
    t,
    setNumPages,
    setIsLoading,
    setShowRefresh,
    handleRefreshList,
    currentPage,
    setCurrentPage,
    totalRecords,
    totalPages,
    handleOnChangePage,
    pdfContainerRef,
    handleCurrentPage,
    handleInputSearch,
  };

  return <HybridMailContext.Provider value={value}>{children}</HybridMailContext.Provider>;
};

export const useHybridMail = () => {
  const context = useContext(HybridMailContext);
  if (!context) {
    throw new Error('useHybridMail must be used within a HybridMailProvider');
  }
  return context;
};
